/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEventType,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, from, Observable, of, switchMap, throwError } from 'rxjs';
import { ContextService } from '../services/context.service';
import { APPSTORE_ORIGIN } from '../injection-token';
import { Octopus } from '@maximizer/core/shared/domain';
import { TokenService } from '../services/token.service';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { Product } from '../models/product.model';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly SessionExpiredPage = '/SessionExpired.aspx';
  private readonly outlookSyncUrl = '/integration/outlook';
  private readonly appStoreUrl = '/appstore/api';

  constructor(
    private readonly token: TokenService,
    private readonly context: ContextService,
    @Inject(APPSTORE_ORIGIN) private readonly appStoreOrigin = '',
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isOutlookProduct = this.context.product === Product.Outlook;
    const shouldHandleWithLegacyDataSvc =
      !isOutlookProduct &&
      request.url.includes('Data.svc') &&
      !request.url.endsWith(Octopus.Action.AUTHENTICATE);
    const shouldHandleWithLegacySyncAuth =
      !isOutlookProduct &&
      (this.isFerretRequest(request) ||
        this.urlIncludes(request, this.outlookSyncUrl));
    const shouldHandleAppStore =
      !isOutlookProduct && this.isAppStoreRequest(request);

    if (
      shouldHandleWithLegacyDataSvc ||
      shouldHandleWithLegacySyncAuth ||
      shouldHandleAppStore
    ) {
      return this.getOctopusRequest(request, next);
    }
    return next.handle(request);
  }

  private getOctopusRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    retryCount = 0,
  ): Observable<HttpEvent<any>> {
    return this.token.getSession().pipe(
      switchMap((response) => {
        if (response.isSessionExpired) {
          location.href = this.context.website + this.SessionExpiredPage;
        }

        const headers = request.headers.set(
          'Authorization',
          'Bearer ' + this.context.token,
        );

        const newRequest = request.clone({ headers });

        return next.handle(newRequest).pipe(
          catchError((error) => {
            if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
              return throwError(() => error);
            }
            if (
              this.urlIncludes(request, this.outlookSyncUrl) ||
              this.isFerretRequest(request)
            ) {
              return of(null);
            }
            if (this.isAppStoreRequest(request) && retryCount < 1) {
              this.context.token = '';
              return this.getOctopusRequest(request, next, retryCount + 1);
            }
            return throwError(() => error);
          }),
          switchMap((event) => {
            if (!event) {
              if (retryCount > 3) {
                return throwError(
                  () => new Error('Maximum authentication retries reached'),
                );
              }
              this.context.token = '';
              return this.getOctopusRequest(request, next, retryCount + 1);
            }
            if (event.type == HttpEventType.Response) {
              if (event.body instanceof Blob) {
                return this.handleBlobResponse(event, request, next);
              }

              if (
                this.speaksOctopus(request) &&
                this.isInvalidAuthentication(event)
              ) {
                this.context.token = '';
                return this.getOctopusRequest(request, next);
              }
            }
            return of(event);
          }),
        );
      }),
    );
  }

  private handleBlobResponse(
    httpResponse: HttpResponse<any>,
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return from(httpResponse.body.text()).pipe(
      switchMap((text) => {
        if (typeof text !== 'string') {
          return of(httpResponse);
        }
        const parsed = JSON.parse(text) as Octopus.Response;
        if (this.isInvalidAuthenticationResponse(parsed)) {
          this.context.token = '';
          return this.getOctopusRequest(request, next);
        }
        return of(httpResponse);
      }),
      catchError(() => of(httpResponse)),
    );
  }

  /**
   * Every handled endpoint gets the Bearer token, but only these answer with an
   * `Octopus.Response`, where `Code` carries the auth vocabulary. Endpoints outside this set
   * use `Code` for their own meanings, so their bodies must not be read as auth failures.
   */
  private speaksOctopus(request: HttpRequest<any>): boolean {
    return (
      request.url.includes('Data.svc') ||
      this.isFerretRequest(request) ||
      this.urlIncludes(request, this.outlookSyncUrl)
    );
  }

  private isInvalidAuthentication(event: HttpEvent<any>): boolean {
    const httpResponse = event as HttpResponse<any>;
    const response = httpResponse.body as Octopus.Response;
    return this.isInvalidAuthenticationResponse(response);
  }

  private isInvalidAuthenticationResponse(response: Octopus.Response): boolean {
    const invalidToken = 'invalid token';

    if (response) {
      const code = response.Code;
      const rawMsg = response.Msg as
        | string
        | string[]
        | Octopus.ResponseError[]
        | undefined;
      const messages = this.toMessageList(rawMsg);

      const invalid = code === Octopus.ResponseStatusCode.InvalidAuthentication;
      const unsuccessful = code === Octopus.ResponseStatusCode.Unsuccessful;

      return (
        invalid ||
        (unsuccessful &&
          messages.some(
            (error: string | Octopus.ResponseError) =>
              (typeof error === 'string' &&
                error.toLowerCase().includes(invalidToken)) ||
              (error as Octopus.ResponseError).Message?.toLowerCase().includes(
                invalidToken,
              ),
          ))
      );
    }
    return false;
  }

  private toMessageList(
    rawMsg: string | string[] | Octopus.ResponseError[] | undefined,
  ): (string | Octopus.ResponseError)[] {
    if (Array.isArray(rawMsg)) {
      return rawMsg;
    }
    return rawMsg ? [rawMsg] : [];
  }

  private isFerretRequest(
    request: HttpRequest<any> | HttpResponse<any>,
  ): boolean {
    return (
      (!isNullOrEmpty(this.context.ferretUrl) &&
        request.url?.startsWith(this.context.ferretUrl)) ??
      false
    );
  }

  private isAppStoreRequest(
    request: HttpRequest<any> | HttpResponse<any>,
  ): boolean {
    return (
      !isNullOrEmpty(this.appStoreOrigin) &&
      (request.url
        ?.toLocaleLowerCase()
        .startsWith(this.appStoreOrigin.toLocaleLowerCase()) ??
        false) &&
      this.urlIncludes(request, this.appStoreUrl)
    );
  }

  private urlIncludes(
    request: HttpRequest<any> | HttpResponse<any>,
    fragment: string,
  ): boolean {
    return request.url?.toLocaleLowerCase().includes(fragment) ?? false;
  }
}
